import React, { useEffect } from 'react'
import { Box, Typography } from '@mui/material'

import { useRef, useState, useLayoutEffect, useId } from 'react'
import { motion, useAnimationControls } from 'framer-motion'

import Arrow from 'atoms/Arrow'
import CountUp from 'react-countup'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})
const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 1,
})

const gradients = {
  blue: ['#2370ED', '#143F87'],
  purple: ['#7B83EB', '#131F8F'],
  red: ['#FF2F32', '#F76A1E'],
  white: ['#fff', '#fff'],
  yellow: ['#FEAD16', '#FF962D'],
  green: ['#91B659', '#406805'],
  gray: ['#a3a7aa', '#a3a7aa'],
}

const MotionBox = motion.create(Box)

function Banner({
  bannerID = 'test',
  color,
  type,
  actionText,
  clickAction = () => console.log('clicked banner'),
  title,
  arrow = true,
  description,
  formatDescription = false,
  counterDescription = false,
  subText,
  content,
  children,
  demoMode = false,
}) {
  const gradientId = useId()

  const [start, end] = gradients[color] || ['#F1F1F1', '#F1F1F1']

  const lastRef = useRef(0)

  useEffect(() => {
    lastRef.current = description
  }, [description])

  const controls = useAnimationControls()

  const textRef = useRef(null)
  const [textWidth, setTextWidth] = useState(0)

  const staticPartsWidth = 30 // icon + padding (approx)

  useLayoutEffect(() => {
    if (textRef.current) {
      const { width } = textRef.current.getBoundingClientRect()
      setTextWidth(width)
    }
  }, [])

  return (
    <MotionBox
      id={bannerID}
      sx={{
        filter: demoMode ? 'grayscale(100%)' : 'none',
        boxShadow:
          type == 'unlock'
            ? 'none'
            : color == 'white'
            ? 'none'
            : `
  0px 4px 25px -8px rgba(0, 0, 0, 0.15),
  0px -2px 12px -6px rgba(0, 0, 0, 0.08)
`,
        cursor: 'pointer',
        '&:hover': {
          filter: demoMode
            ? 'brightness(0.9) grayscale(100%)'
            : 'brightness(0.9)',
        },

        background: `linear-gradient(135deg, ${start}, ${end})`,
        borderBottom: color == 'white' ? '1px solid #f1f1f1' : 'none',
        padding: { xs: '18px', md: '24px' },
        borderRadius:
          type == 'header'
            ? { xs: '12px 12px 0px 0px ', md: '24px 24px 0px 0px ' }
            : { xs: '12px', md: '24px' },
        position: 'relative',
        overflow: 'hidden',
      }}
      whileHover="hover"
      onHoverStart={() => {
        if (!demoMode) {
          controls.start({
            width: textWidth + staticPartsWidth,
            transition: { type: 'spring', stiffness: 300, damping: 24 },
          })
          controls.start('show')
        }
      }}
      onHoverEnd={() => {
        controls.start({
          width: 30,
          transition: { type: 'spring', stiffness: 300, damping: 24 },
        })
        controls.start('hide')
      }}
      onClick={clickAction}
    >
      {children ? (
        <>{children}</>
      ) : (
        <>
          {arrow && (
            <MotionBox
              initial={{ width: 30 }}
              animate={controls}
              sx={{
                zIndex: 20,

                position: 'absolute',
                top: 14,
                right: 14,
                height: '30px',
                backgroundColor:
                  type == 'unlock'
                    ? '#262627'
                    : color == 'white'
                    ? '#A3A8AA'
                    : // Blue - temp changing to gray for Net Worth / Add Account Click
                      // ? '#2370ED'
                      '#fff',
                borderRadius: '50px',
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                boxShadow: '0px 4px 25px -8px #00000033',
              }}
            >
              <motion.div
                ref={textRef}
                variants={{
                  show: { opacity: 1, x: 0 },
                  hide: { opacity: 0, x: -10 },
                }}
                initial="hide"
                animate={controls}
                transition={{ duration: 0.25 }}
                style={{
                  paddingTop: '2px',
                  paddingRight: '20px',
                  fontSize: '12px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  marginRight: 'auto',
                  pointerEvents: 'none',
                }}
              >
                <Typography
                  variant="body2Fun"
                  sx={{
                    background: `linear-gradient(135deg, ${start}, ${end})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {actionText}
                </Typography>
              </motion.div>

              {/* Arrow */}

              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '30px',
                  height: '30px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  backgroundColor:
                    type == 'unlock'
                      ? '#262627'
                      : color == 'white'
                      ? '#A3A8AA'
                      : // Blue - temp changing to gray for Net Worth / Add Account Click
                        // ? '#2370ED'
                        '#fff',
                }}
              >
                <Arrow color={color} />
              </Box>
            </MotionBox>
          )}

          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              flexDirection: 'row',
              justifyContent: 'space-between',
              //   borderRadius: '10px',
              color:
                type == 'unlock'
                  ? '#262627'
                  : color == 'white'
                  ? '#262627'
                  : '#fff',
            }}
          >
            {/* left side text */}
            <Box
              sx={{
                zIndex: 10,
                flexShrink: content ? 0 : 1,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left',
                gap: '8px',
                justifyContent: 'flex-start',
                maxWidth: '100%',
              }}
            >
              {title && <Typography variant="body2Fun">{title}</Typography>}

              {/* if there is no title make text two lines and not one*/}

              {description &&
                (title === '' ? (
                  <>
                    <Typography variant="headerTitle">{description}</Typography>
                    {subText && (
                      <Typography variant="body3Weird">{subText}</Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="headerTitle" component="div">
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'flex-end',
                        gap: '4px',
                      }}
                    >
                      {counterDescription ? (
                        <>
                          {!formatDescription && '$'}
                          <CountUp
                            start={lastRef.current}
                            end={description}
                            separator=","
                            formattingFn={(val) => {
                              if (formatDescription) {
                                return `${
                                  val > 999999
                                    ? `${dollarCentsFormat.format(
                                        val / 1000000
                                      )}M`
                                    : `${dollarFormat.format(val / 1000)}k`
                                }`
                              } else {
                                if (val > 0) {
                                  return val.toLocaleString()
                                } else {
                                  return val
                                }
                              }
                            }}
                            duration={0.5}
                          />
                        </>
                      ) : (
                        <>{description}</>
                      )}

                      {subText === 'up' ? (
                        <svg
                          width="19"
                          height="10"
                          viewBox="0 0 19 10"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            display: 'block',
                            alignSelf: 'center',
                          }}
                        >
                          <path
                            d="M18.3884 9.13964L0.614746 9.17383L9.50255 0.28996L18.3884 9.13964Z"
                            fill="#60BE64"
                          />
                        </svg>
                      ) : (
                        <Typography
                          pb={0.5}
                          variant="body3Weird"
                          component="span"
                        >
                          {subText}
                        </Typography>
                      )}
                    </Box>
                  </Typography>
                ))}
            </Box>

            {/* right side extras */}
            <Box
              sx={{
                display: 'flex',
                position: 'relative',
                flexGrow: 1,
                flexShrink: 0,
                justifyContent: 'center',
              }}
            >
              {content && (
                <Box
                  style={{
                    maxWidth: '100%',
                    width: 1200,
                    // top: 0,
                    // right: 0,
                    zIndex: 1,
                    // paddingTop: '48px',
                    paddingRight: '48px',
                    paddingLeft: '48px',
                    position: 'absolute',
                    alignSelf: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {content}
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </MotionBox>
  )
}

export default Banner
